import { useCallback, useEffect, useRef, useState } from 'react';
import { getAttendanceHistory } from '../services/attendanceService';
import { getMonthlyCalendar } from '../services/officeCalendarService';
import type {
  AttendanceHistoryFilters,
  AttendanceHistoryItem,
} from '../types/attendance';
import type { HolidayRecord } from '../types/officeCalendar';
import { attendanceErrorMessage } from '../utils/attendanceErrors';
import { createDefaultAttendanceFilters } from '../utils/attendanceFilters';
import {
  mergeDayStatusItems,
  mergeHolidaysIntoHistoryItems,
} from '../utils/attendanceHolidays';
import { parseYearMonth } from '../utils/attendanceCalendar';

/** Cover a full calendar month in one page for history/calendar UI. */
const DEFAULT_PAGE_SIZE = 100;

export function useAttendanceHistory(options: { employeeId?: number } = {}) {
  const employeeId =
    typeof options.employeeId === 'number' &&
    Number.isInteger(options.employeeId) &&
    options.employeeId > 0
      ? options.employeeId
      : undefined;

  const [items, setItems] = useState<AttendanceHistoryItem[]>([]);
  const [holidays, setHolidays] = useState<HolidayRecord[]>([]);
  const [filters, setFiltersState] = useState<AttendanceHistoryFilters>(
    createDefaultAttendanceFilters(),
  );
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  const requestId = useRef(0);
  const loadingMore = useRef(false);
  const pageRef = useRef(1);
  const filtersRef = useRef(filters);
  const employeeIdRef = useRef(employeeId);
  employeeIdRef.current = employeeId;

  const fetchPage = useCallback(
    async (nextPage: number, mode: 'replace' | 'append' | 'refresh') => {
      const currentRequest = ++requestId.current;
      if (mode === 'append') {
        if (loadingMore.current) return;
        loadingMore.current = true;
        setIsLoadingMore(true);
      } else if (mode === 'refresh') {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const { year, monthIndex } = parseYearMonth(filtersRef.current.fromDate);
        const [result, calendarResult] = await Promise.all([
          getAttendanceHistory({
            page: nextPage,
            pageSize: DEFAULT_PAGE_SIZE,
            filters: filtersRef.current,
            employeeId: employeeIdRef.current,
          }),
          getMonthlyCalendar(year, monthIndex + 1).catch(() => null),
        ]);

        if (!mounted.current || currentRequest !== requestId.current) return;

        const resolvedHolidays =
          result.holidays?.length
            ? result.holidays
            : calendarResult?.holidays ?? [];

        setHolidays(resolvedHolidays);

        setPage(result.page);
        pageRef.current = result.page;
        setHasMore(result.page < result.totalPages);
        setItems(current => {
          const attendanceItems =
            mode === 'append'
              ? (() => {
                  // Filled-in days (no attendanceId) are rebuilt below; drop them
                  // so a later page's real record for that date is not hidden.
                  const recorded = current.filter(item => item.attendanceId != null);
                  const seen = new Set<number | string>(
                    recorded.map(item => item.attendanceId ?? item.attendanceDate),
                  );
                  const merged = [...recorded];
                  for (const item of result.items) {
                    const key = item.attendanceId ?? item.attendanceDate;
                    if (!seen.has(key)) merged.push(item);
                  }
                  return merged;
                })()
              : result.items;

          return mergeHolidaysIntoHistoryItems(
            mergeDayStatusItems(
              attendanceItems,
              result.days ?? [],
              resolvedHolidays,
            ),
            resolvedHolidays,
            filtersRef.current.fromDate,
            filtersRef.current.toDate,
          );
        });
      } catch (caught) {
        if (mounted.current && currentRequest === requestId.current) {
          setError(
            attendanceErrorMessage(
              caught,
              'Unable to load attendance history.',
            ),
          );
        }
      } finally {
        loadingMore.current = false;
        if (mounted.current && currentRequest === requestId.current) {
          setIsLoading(false);
          setIsRefreshing(false);
          setIsLoadingMore(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const applyFilters = useCallback(
    (next: AttendanceHistoryFilters) => {
      setFiltersState(next);
      filtersRef.current = next;
      pageRef.current = 1;
      fetchPage(1, 'replace');
    },
    [fetchPage],
  );

  const resetFilters = useCallback(() => {
    const defaults = createDefaultAttendanceFilters();
    setFiltersState(defaults);
    filtersRef.current = defaults;
    pageRef.current = 1;
    fetchPage(1, 'replace');
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore.current || isLoading || isRefreshing) return;
    fetchPage(pageRef.current + 1, 'append');
  }, [fetchPage, hasMore, isLoading, isRefreshing]);

  const loadHistory = useCallback(() => fetchPage(1, 'replace'), [fetchPage]);
  const refreshHistory = useCallback(
    () => fetchPage(1, 'refresh'),
    [fetchPage],
  );
  const retry = useCallback(() => fetchPage(1, 'replace'), [fetchPage]);

  return {
    items,
    holidays,
    filters,
    page,
    isLoading,
    isRefreshing,
    isLoadingMore,
    hasMore,
    error,
    loadHistory,
    refreshHistory,
    loadMore,
    applyFilters,
    resetFilters,
    retry,
  };
}
